import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { io } from 'socket.io-client';

import { QueryKey } from '../constants/index';
import { HttpException } from '../exceptions/index';
import { Company, NotificationHooks, useInvalidateQueries, User } from '../hooks/index';
import { useSafeConversionProgressStore } from '../pages/safes/convert-modal/conversion-progress.store';
import { SAFE_CONVERSION_RELATED_QUERY_KEYS } from '../pages/safes/hooks/use-safe-conversion-progress-fallback';
import { SOCKET_IO_CLIENT_BASE_OPTIONS } from '../sockets/socket-io-client-options';
import { ensureFreshAccessTokenForWs } from '../sockets/ws-ensure-access-token.util';
import { accessTokenStorage } from '../storage/auth/index';
import { AppNotification, NotificationType } from '../types/notification.types';
import { getScenarioSocketUrl } from '../utils/global.util';
import { toNumber } from '../utils/to-number.util';
import { toPositive } from '../utils/to-positive.util';

interface NotificationsContext {
  handleReadAllNotification: () => void;
  handleReadNotification: (type: NotificationType) => void;
  refreshUnreadCount: () => void;
  unreadNotification: { total: number } & Record<NotificationType, number>;
}

const initialValue: NotificationsContext = {
  unreadNotification: { activity: 0, total: 0, request: 0 },
  handleReadNotification() {},
  handleReadAllNotification() {},
  refreshUnreadCount() {},
};

export const notificationsContext = createContext(initialValue);

export interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: FC<NotificationsProviderProps> = ({ children }) => {
  const { selectedCompanyId: companyId } = Company.useSelected();
  const { user } = User.useUser();

  const {
    unread,
    dataUpdatedAt: totalUpdatedAt,
    isLoading,
  } = NotificationHooks.useUnreadNotificationCount({
    companyId,
    aminInAppNotifications: user?.adminInAppNotifications,
    stakeholderInAppNotifications: user?.stakeholderInAppNotifications,
  });

  const {
    unread: activityUnread,
    dataUpdatedAt: activityUpdatedAt,
    isLoading: isLoadingActivity,
  } = NotificationHooks.useUnreadNotificationCount({
    companyId,
    type: NotificationType.ACTIVITY,
    aminInAppNotifications: user?.adminInAppNotifications,
    stakeholderInAppNotifications: user?.stakeholderInAppNotifications,
  });

  const {
    unread: requestUnread,
    dataUpdatedAt: requestUpdatedAt,
    isLoading: isLoadingRequest,
  } = NotificationHooks.useUnreadNotificationCount({
    companyId,
    type: NotificationType.REQUEST,
    aminInAppNotifications: user?.adminInAppNotifications,
    stakeholderInAppNotifications: user?.stakeholderInAppNotifications,
  });

  const [unreadNotification, setUnread] = useState<
    { total: number } & Record<NotificationType, number>
  >({
    activity: 0,
    total: 0,
    request: 0,
  });

  // Keyed on `dataUpdatedAt`, not on the counts: the optimistic +1/-1 below mutates this mirror
  // independently of the query cache, so a refetch that returns the same number it returned last
  // time changes no count dependency and would leave the drift in place. Every successful read
  // must overwrite the mirror with server truth.
  useEffect(() => {
    if (isLoading || isLoadingActivity || isLoadingRequest) return;

    setUnread({
      request: toNumber(requestUnread.count),
      activity: toNumber(activityUnread.count),
      total: toNumber(unread.count),
    });
  }, [
    activityUnread.count,
    activityUpdatedAt,
    isLoading,
    isLoadingRequest,
    isLoadingActivity,
    requestUnread.count,
    requestUpdatedAt,
    unread.count,
    totalUpdatedAt,
  ]);

  const handleReadNotification = (type: NotificationType) =>
    setUnread((prev) => ({
      ...prev,
      [type]: toPositive(prev[type] - 1),
      total: toPositive(prev.total - 1),
    }));

  const handleReadAllNotification = () =>
    setUnread({
      activity: 0,
      total: 0,
      request: 0,
    });

  const queryClient = useQueryClient();

  // The badge is a locally-mutated counter (+1 per websocket push, -1 per mark-read) that only
  // re-syncs when the unread-count query refetches. Notifications can also vanish server-side with
  // no push and no mutation of ours — deleting an event cascades vesting task → exercise request →
  // request notification, and another admin can resolve a request — so anything that surfaces the
  // counts to the user must force a re-read first, or the badge keeps the pre-delete number while
  // the list already shows one fewer.
  const refreshUnreadCount = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_UNREAD_NOTIFICATION_COUNT] });
  }, [queryClient]);

  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_NOTIFICATIONS);
  const invalidateNotificationsDebouncedRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    const buildAuthPayload = () => ({
      isAdmin: false as const,
      accessToken: accessTokenStorage.getState().get() || '',
    });

    const socket = io(getScenarioSocketUrl('notifications'), {
      ...SOCKET_IO_CLIENT_BASE_OPTIONS,
      auth: (cb) => {
        void ensureFreshAccessTokenForWs().finally(() => {
          cb(buildAuthPayload());
        });
      },
    });

    socket.on('connect_error', (error) => console.error('Connection error:', error));
    socket.on('connect', () => console.log('Notifications ws connected'));
    socket.on('disconnect', (reason) => console.log('Notifications ws disconnect', reason));
    socket.on('error', (e: HttpException) => console.error(e));
    socket.on('notification', (notificationString: string) => {
      const { type } = JSON.parse<Pick<AppNotification, 'id' | 'type'>>(notificationString);
      setUnread((prev) => ({
        ...prev,
        total: prev.total + 1,
        [type]: toNumber(prev[type] + 1),
      }));

      clearTimeout(invalidateNotificationsDebouncedRef.current);
      invalidateNotificationsDebouncedRef.current = setTimeout(() => {
        invalidateNotificationsDebouncedRef.current = undefined;
        invalidateQuery();
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_UNREAD_NOTIFICATION_COUNT] });
      }, 500);
    });
    socket.on('access-role-changed', () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SELECTED_COMPANY] });
    });
    socket.on('safe-conversion-completed', () => {
      useSafeConversionProgressStore.getState().clearConverting();
      for (const queryKey of SAFE_CONVERSION_RELATED_QUERY_KEYS) {
        void queryClient.invalidateQueries({ queryKey: [queryKey] });
      }
    });

    return () => {
      clearTimeout(invalidateNotificationsDebouncedRef.current);
      socket.disconnect();
    };
  }, [invalidateQuery, queryClient]);

  return (
    <notificationsContext.Provider
      value={{
        unreadNotification,
        handleReadNotification,
        handleReadAllNotification,
        refreshUnreadCount,
      }}
    >
      {children}
    </notificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(notificationsContext);
